// A JSON call to a Nexus /auth/* endpoint with the current bearer token. One retry across a silent refresh on 401
// (the hand-off step-up poll can outlive a 15-minute access token). Never throws for an HTTP status: the caller reads
// {status, body} — the step-up and device-approve flows branch on the gateway's `code`, not on exceptions.

import { AUTH_URL } from './config';
import { getRefreshToken, getToken, refreshAccess } from './api';
import { hasStepUpAmr } from './handoff';

export interface AuthReply { status: number; body: Record<string, unknown> }
export type AuthCaller = (method: 'GET' | 'POST' | 'DELETE', path: string, body?: unknown) => Promise<AuthReply>;

export const codeOfReply = (r: AuthReply): string | undefined => (typeof r.body.code === 'string' ? r.body.code : undefined);

export const authCall: AuthCaller = async (method, path, body) => {
  const usedRefresh = getRefreshToken();
  const send = async (): Promise<AuthReply> => {
    const res = await fetch(AUTH_URL(path), {
      method,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', Authorization: `Bearer ${getToken() ?? ''}` },
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
    });
    let j: unknown = {};
    try { const t = await res.text(); j = t ? JSON.parse(t) : {}; } catch { j = {}; }
    return { status: res.status, body: j && typeof j === 'object' && !Array.isArray(j) ? (j as Record<string, unknown>) : {} };
  };
  let r = await send();
  if (r.status === 401 && usedRefresh && (await refreshAccess(usedRefresh)) === 'ok') r = await send();
  return r;
};

/**
 * L2: the step-up poll said ALREADY_USED — an earlier poll (another tab, a lost response) consumed the approval.
 * The session row already has it, so refresh the access token; when the new token carries amr `mfa` or
 * `handoff_headset`, that is the success we missed. Null otherwise.
 */
export async function recoverAfterUsed(): Promise<string | null> {
  const rt = getRefreshToken();
  if (!rt) return null;
  if ((await refreshAccess(rt)) !== 'ok') return null;
  const t = getToken();
  return hasStepUpAmr(t) ? t : null;
}
